/**
 * A key of a {@link NextCall} method responsible for calling the next pass in chain.
 */
const NextCall__symbol = ( /*#__PURE__*/Symbol('next-call'));
/**
 * Constructs a call of the next pass in chain.
 *
 * @typeParam TChain - A type of supported call chain.
 * @typeParam TNextArgs - A type of argument tuple of the next pass.
 * @typeParam TNextArg - A type of single argument or arguments tuple of the next pass.
 * The same as `TNextArgs` by default.
 * @param callNext - A next pass caller function.
 *
 * @returns Next pass call performed by the given function.
 */
function nextCall(callNext) {
    const result = (() => result);
    result[NextCall__symbol] = (chain, fn) => callNext(chain, fn);
    return result;
}
/**
 * Checks whether the `target` value is a {@link NextCall next call}.
 *
 * @typeParam TChain - A type of supported call chain.
 * @typeParam TNextArgs - A type of arguments tuple of the next pass.
 * @typeParam TNextArg - A type of single argument or arguments tuple of the next pass.
 * @param target - A value to check.
 *
 * @returns `true` if the `target` value is a function with {@link NextCall__symbol} property, or `false` otherwise.
 */
function isNextCall(target) {
    return typeof target === 'function' && NextCall__symbol in target;
}

function callThru(...passes) {
    return (...args) => {
        let result;
        const chain = (index) => {
            const lastPass = index >= passes.length;
            ++index;
            const pass = index < passes.length ? passes[index] : callThru$noop;
            const handleResult = (callResult, arg) => {
                if (isNextCall(callResult)) {
                    callResult[NextCall__symbol](chain(index), pass);
                }
                else if (lastPass) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    result = arg;
                }
                else {
                    chain(index).pass(pass, callResult);
                }
            };
            return ({
                call(fn, args) {
                    handleResult(fn(...args), args);
                },
                pass(fn, arg) {
                    handleResult(fn(arg), arg);
                },
                skip(r) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    result = r;
                },
            });
        };
        chain(0).call(passes[0], args);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return result;
    };
}
/**
 * @internal
 */
function callThru$noop(..._args) {
    /* noop */
}

/**
 * Provides a single argument for the next call chain pass.
 *
 * The returned {@link NextCall} implementation does the same as if the `arg` returned from the call chain pass.
 *
 * @typeParam TNextArg - A type of argument for the next pass.
 * @param arg - Argument for the next pass.
 *
 * @return A call of the next pass with the given argument.
 */
function nextArg(arg) {
    return nextCall((chain, pass) => chain.pass(pass, arg));
}

/**
 * Provides arguments for the next call chain pass.
 *
 * @typeParam TNextArgs - A type of arguments tuple for the next pass.
 * @param args - Arguments for the next pass.
 *
 * @return A call of the next pass with the given arguments.
 */
function nextArgs(...args) {
    return nextCall((chain, fn) => chain.call(fn, args));
}

/**
 * Builds a next chained call that passes each provided element to the next pass.
 *
 * Note that default {@link CallChain} supported by {@link callThru} would return only the last element.
 *
 * @typeParam T - A type of element to pass down the chain.
 * @param elements - An iterable of elements to pass down the chain.
 *
 * @returns A multi-call of the next pass with each element.
 */
function nextEach(elements) {
    return nextCall((chain, pass) => {
        for (const element of elements) {
            chain.pass(pass, element);
        }
    });
}

/**
 * Builds a next call that skips the rest of the chain and returns the given value as call chain result.
 *
 * Not every call chain returns a result. In that case the {@link nextSkip} is a better choice.
 *
 * @typeParam TResult - Call chain result type.
 * @param result - Call chain result.
 *
 * @returns Next call skipping the rest of the passes.
 */
function nextReturn(result) {
    return nextCall(chain => chain.skip(result));
}

/**
 * Builds a next call that skips the rest of the chain.
 *
 * This has the same effect as {@link nextReturn `nextReturn(undefined)`}.
 */
const nextSkip = ( /*#__PURE__*/nextCall(chain => chain.skip()));

export { NextCall__symbol, callThru, isNextCall, nextArg, nextArgs, nextCall, nextEach, nextReturn, nextSkip };
//# sourceMappingURL=call-thru.js.map
